import React, { useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Reveal from '../../components/Reveal/Reveal'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import AbstractVisual from '../../components/AbstractVisual/AbstractVisual'
import { pagePhotos } from '../../data/photos'
import allIndustries from '../../data/industries'
import { localizeIndustries } from '../../data/localizeIndustry'
import { SITE_URL } from '../../config/site'

import '../services/services.scss'
import './industries.scss'

const pad = (n) => String(n).padStart(2, '0')

/** /industries — the hub linking every industry page. */
const Industries = () => {
  const { translations } = useContext(LanguageContext)
  const animateIn = useEntryMotion()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const hero = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 40 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  const industriesLabel = translations.navIndustries || 'Industries'
  const industries = localizeIndustries(allIndustries, translations)

  return (
    <Layout>
      <Seo
        title='Industries We Serve: Healthcare, Fintech & More'
        description='AI and software development by industry: healthcare and medical imaging, architecture and construction, fintech and payments, e-commerce and retail.'
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Industries', path: '/industries' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Industries MLMind serves',
            itemListElement: allIndustries.map((industry, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: industry.name,
              url: `${SITE_URL}/industries/${industry.slug}`,
            })),
          },
        ]}
      />

      <section className='svc-hero'>
        <div className='svc-inner'>
          <nav className='svc-breadcrumb' aria-label='Breadcrumb'>
            <Link to='/'>{translations.uiHome}</Link>
            <span aria-hidden='true'>/</span>
            <span aria-current='page'>{industriesLabel}</span>
          </nav>

          <div className='svc-hero-grid'>
            <div className='svc-hero-copy'>
              <motion.p className='svc-eyebrow' {...hero(0)}>{industriesLabel}</motion.p>
              <motion.h1 className='svc-hero-title' {...hero(0.08)}>{translations.indHubTitle || 'Software built for how your industry works'}</motion.h1>
              <motion.p className='svc-hero-tagline' {...hero(0.16)}>{translations.indHubTagline || 'AI, web and mobile products for the sectors where we have shipped real work.'}</motion.p>
              <motion.p className='svc-hero-text' {...hero(0.24)}>
                {translations.indHubText || 'Every industry has its own data, rules and ways of working. We focus on the ones we know from delivered projects, so the first conversation is about your problem — not about explaining your business to us.'}
              </motion.p>
              <motion.div className='svc-actions' {...hero(0.32)}>
                <Link to='/contact-us' className='svc-btn svc-btn-primary'>{translations.svcHeroPrimary}</Link>
                <Link to='/projects' className='svc-btn svc-btn-ghost'>
                  {translations.svcHeroSecondary} <span aria-hidden='true'>→</span>
                </Link>
              </motion.div>
            </div>
            <motion.div className='svc-hero-visual-wrap' {...hero(0.2)}>
              {pagePhotos.industries ? (
                <img className='svc-hero-visual svc-hero-photo' src={pagePhotos.industries} alt='' />
              ) : (
                <AbstractVisual className='svc-hero-visual' shape='ring' />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <div className='ind-hub-grid'>
            {industries.map((industry, index) => (
              <Reveal key={industry.slug} delay={(index % 2) * 0.08} className='ind-hub-wrap'>
                <Link to={`/industries/${industry.slug}`} className='svc-link-card ind-hub-card'>
                  <span className='svc-deep-num'>{pad(index + 1)}</span>
                  <h2 className='svc-card-title ind-hub-title'>{industry.name}</h2>
                  <p className='svc-card-text'>{industry.summary}</p>
                  <ul className='ind-hub-tags'>
                    {industry.solutions.slice(0, 3).map((solution) => <li key={solution.title}>{solution.title}</li>)}
                  </ul>
                  <span className='svc-link-more'>{translations.uiLearnMore} <span aria-hidden='true'>→</span></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section svc-cta'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow svc-eyebrow-light'>{translations.svcCtaEyebrow}</p>
            <h2 className='svc-cta-title'>{translations.svcCtaTitle}</h2>
            <p className='svc-cta-text'>{translations.svcCtaText}</p>
            <Link to='/contact-us' className='svc-btn svc-btn-light'>{translations.svcCtaButton}</Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  )
}

export default Industries
